<template>
  <div class="flex flex-wrap">
    <div class="flex-1 md:flex-none md:w-full pr-10 mdsm:pr-0">
      <section>
        <div class="container">
          <div class="mb-10">
            <slot name="section-title"></slot>
            <p class="lg:pr-10">Please complete the details below and one of your experienced cruise designers will contact you shortly
              to create your bespoke, once-in-a lifetime cruise in {{supplier.country}}.</p>
          </div>
          <div class="mb-10">
            <h1 class="fs-20 mb-6 font-bry font-bold">Enquiry overview</h1>
            <div class="mb-6 flex items-center font-bold font-bry">
              <svg fill="currentColor" class="mr-4" width="24" height="24"><use xlink:href="/img/sprite.svg#ic_place" /></svg>
              {{ itinerary.charter_itinerary.itinerary_destination_text }}
            </div>
            <div class="mb-6 flex items-center font-bold font-bry">
              <svg fill="currentColor" class="mr-4" width="24" height="24"><use xlink:href="/img/sprite.svg#ic_charter-yacht" /></svg>
              {{ supplier.name }}
            </div>
            <div class="mb-6 flex items-center font-bold font-bry">
              <svg fill="currentColor" class="mr-4" width="24" height="24"><use xlink:href="/img/sprite.svg#ic_itinerary" /></svg>
              {{ itinerary.charter_itinerary.name }}
            </div>
            <div class="flex items-center font-bold font-bry">
              <svg fill="currentColor" class="mr-4" width="24" height="24"><use xlink:href="/img/sprite.svg#ic_double-bed" /></svg>
              {{supplier.number_of_cabins}} {{supplier.number_of_cabins > 1 ? 'cabins' : 'cabin'}}, up to {{ supplier.max_occupancy }} guests
            </div>
          </div>
          <div class="enquiry-form">
            <h1 class="fs-20 mb-8 font-bry font-bold">Your details</h1>
            <validation-observer tag="div" ref="valiObjserver">
              <form @submit.prevent="onBeforeSubmit">
                <label class="block mb-8">
                  <p class="mb-2 fs-14">First name</p>
                  <validation-provider
                    ref="First Name"
                    name="First Name"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <input
                      class="input input-tall w-full"
                      :class="{ 'input--error': errors[0] }"
                      v-model="firstName"
                    />
                    <span class="fs-14 text-danger mt-1">{{ errors[0] }}</span>
                  </validation-provider>
                </label>

                <label class="block mb-8">
                  <p class="mb-2 fs-14">Last name</p>
                  <validation-provider
                    ref="Last Name"
                    name="Last Name"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <input
                      class="input input-tall w-full"
                      :class="{ 'input--error': errors[0] }"
                      v-model="lastName"
                    />
                    <span class="fs-14 text-danger mt-1">{{ errors[0] }}</span>
                  </validation-provider>
                </label>

                <label class="block mb-8">
                  <p class="mb-2 fs-14">Email</p>
                  <validation-provider
                    ref="Email"
                    name="Email"
                    rules="required|email"
                    v-slot="{ errors }"
                  >
                    <input
                      class="input input-tall w-full"
                      :class="{ 'input--error': errors[0] }"
                      v-model.lazy="email"
                    />
                    <span class="fs-14 text-danger mt-1">{{ errors[0] }}</span>
                  </validation-provider>
                </label>

                <div class="mb-8 w-3/5 sm:w-full">
                  <label class="mb-2 fs-14 inline-block">Select country</label>
                  <custom-select
                    v-model="country"
                    placeholder="Select country"
                    :options="countryOptions"
                  />
                </div>

                <label class="block mb-8">
                  <p class="mb-2 fs-14">Phone (optional)</p>
                  <phone-input
                    class="input input-tall w-full"
                    v-model="phone"
                  />
                </label>

                <div class="mb-8 w-4/5 sm:w-full">
                  <label class="mb-2 fs-14 inline-block" for="inputDate">Travel date</label>
                  <tooltip name="ttDates" ref="ttDates">
                    <template v-slot:trigger="{ toggle, isShowing }">
                      <div class="relative">
                        <validation-provider
                          ref="Dates"
                          name="Dates"
                          rules="required"
                          v-slot="{ errors }"
                          slim
                        >
                          <input
                            v-model="checkIn"
                            class="h-0 block"
                            :class="{ 'input-booking-date-error': errors[0] }"
                          />
                        </validation-provider>

                        <div id="inputDate" class="flex items-center">
                          <svg
                            width="24"
                            height="24"
                            class="flex-none booking-widget-input-icon--left"
                          >
                            <use xlink:href="/img/sprite.svg#ic_calendar"></use>
                          </svg>
                          <input
                            type="text"
                            tabindex="100"
                            readonly
                            placeholder="Add travel date"
                            :value="dateDisplay"
                            class="cursor-pointer rounded input-tall w-full border border-black-30 booking-widget-input"
                            :class="{ 'force-focus': isShowing }"
                            @click="isMobile ? openMobileBookingWidget(1) : toggle()"
                          />
                        </div>

                        <p
                          id="inputDateMessage"
                          class="fs-14 text-danger mt-1 hidden"
                          v-if="!isShowing"
                        >
                          Please enter your booking dates
                        </p>
                      </div>
                    </template>

                    <template>
                      <div class="px-5 pt-6 pb-6 bg-white rounded mt-2 md:rounded-none mdsm:p-0">
                        <zb-datepicker v-model="checkIn"></zb-datepicker>
                      </div>
                    </template>
                  </tooltip>
                </div>

                <div class="mb-8 w-3/5 sm:w-full">
                  <label class="mb-2 fs-14 inline-block">Length of trip</label>
                  <custom-select
                    v-model="nightsNum"
                    placeholder="Select nights"
                    :options="nightsNums"
                  />
                </div>

                <label>
                  <p class="mb-2 fs-14">Message (optional)</p>
                  <textarea
                    class="input w-full enquiry-additional-request"
                    v-model="guestNote"
                  />
                </label>

                <button
                  class="mt-8 btn btn-primary input-tall font-medium sm:w-full sm:justify-center flex items-center"
                  :disabled="isWorking"
                  :class="isWorking ? 'loading' : ''"
                >
                  <div class="flex justify-center">
                    <loading-indicator width="20" class="mr-2" v-show="isWorking" />
                    <span>Send enquiry</span>
                  </div>
                </button>
              </form>
            </validation-observer>
          </div>
        </div>
      </section>

      <hr class="spacer-standard border border-black-30 mdsm:hidden" />
    </div>

    <div class="enquiry-direct-sidebar md:mt-16 sm:mt-12 sm:w-full">
      <div class="box p-0 bg-white">
        <image-carousel
          :images="itineraryImages"
          v-if="itineraryImages.length > 1"
        />
        <img
          v-else
          class="enquiry-direct-resort-image w-full"
          :src="itinerary.charter_itinerary.image"
          :alt="itinerary.charter_itinerary.image"
        />

        <div class="p-6">
          <h4 class="fs-20 font-bry font-bold">{{ itinerary.charter_itinerary.name }}</h4>
          <p class="text-black-light-5 fs-14">{{ itinerary.charter_itinerary.itinerary_destination_text }}</p>
          <div class="standard-list fs-16 my-8 mt-4" v-html="itinerary.charter_itinerary.highlights" />
          <hr class="border border-black-30 my-6">
          <div class="font-bry">
            from 
            <span class="fs-20 font-bold">
              <currency-context>
                <template v-slot="{ convert }">
                  <span v-text="convert(itinerary.price_per_night)"></span>
                </template>
              </currency-context>
            </span> /day
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ValidationProvider,
  ValidationObserver,
  extend,
  setInteractionMode
} from 'vee-validate';
import { required, email, min_value } from 'vee-validate/dist/rules';
import get from 'lodash/get';

import LoadingIndicator from '../../components/core/LoadingIndicator';
import Tooltip from '../../components/core/Tooltip';
import PhoneInput from '../../components/core/PhoneInput';
import DatePicker from '../../components/core/DatePicker';
import GuestPicker from '../../components/core/Booking/DetailedGuestPicker';
import RoomPicker from '../../components/core/Booking/RoomPicker';
import EnquiryDirectMobileBookingWidget from '../../components/core/Booking/EnquiryDirectMobileBookingWidget';
import CustomSelect from '../../components/core/CustomSelect';
import {COUNTRY_CODES} from "../../helpers/constants";
import ImageCarousel from './../../components/ImageCarousel/ImageCarousel'
import CurrencyContext from "../../components/core/CurrencyContext";

setInteractionMode('eager');
extend('email', email);
extend('min_value', min_value);
extend('required', {
  ...required,
  message: field => `Please enter your ${field}`
});

const fieldsOrder = [
  'First Name',
  'Last Name',
  'Email',
  'Phone',
  'Dates',
  'Guests',
  'Rooms'
];

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    PhoneInput,
    CustomSelect,
    'zb-datepicker': DatePicker,
    'zb-guest-picker': GuestPicker,
    'zb-room-picker': RoomPicker,
    EnquiryDirectMobileBookingWidget,
    Tooltip,
    LoadingIndicator,
    ImageCarousel,
    CurrencyContext
  },
  name: 'CharterYacht',
  data() {
    return {
      isWorking: false,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      country: '',
      checkIn: moment(),
      nightsNum: '',
      guestNote: '',
      validations: {
        moreRoomsThanGuests: false
      },
      formSubmitted: false,
      itinerary: {
        charter_itinerary: {},
        price_per_night: 0
      },
      supplier: {}
    };
  },
  computed: {
    nightsNums() {
      return [
        { value: 1, label: '1' },
        { value: 2, label: '2' },
        { value: 3, label: '3' },
        { value: 4, label: '4' },
        { value: 5, label: '5' },
        { value: 6, label: '6' },
        { value: 7, label: '7' },
        { value: 8, label: '8' },
        { value: 9, label: '9' },
        { value: 10, label: '10' },
      ];
    },
    countryOptions() {
      return COUNTRY_CODES.map(item => ({
        value: item.code,
        label: `${item.name} (${item.dial_code})`
      }))
    },
    isMobile() {
      return window.matchMedia('only screen and (max-width: 1200px)').matches;
    },
    dateDisplay() {
      return moment(this.checkIn).format('MMM DD, YYYY')
    },
    itineraryImages() {
      const itineraryImage = get(this.itinerary, 'charter_itinerary.image')
      const activitiesImages = get(this.itinerary, 'charter_itinerary.activities', []).map(item => item.activity_image)

      return [
        itineraryImage,
        ...activitiesImages
      ]
    }
  },
  mounted() {
    // https://zubludiving.atlassian.net/wiki/spaces/EN/pages/371654686/Widget+Integration
    const queries = new URLSearchParams(location.search);
    const supplierId = queries.get('id')
    const itineraryId = queries.get('itineraryId')

    if (supplierId) {
      this.getSupplierInfo(supplierId, itineraryId)
    }
  },
  created() {
    window.addEventListener('beforeunload', this.sendGoogleAnalytics);
  },
  methods: {
    sendGoogleAnalytics() {
      if (!this.formSubmitted) {
        // sendGAEvent('Leave without Submit','Contact us - Leave Without Submit',');

        window.pushDataLayer('zublu_cta', {
          cta_type: 'enquiry',
          cta_value: 'enquiry_leave',
          cta_screen: window.location.pathname
        });
      }
    },
    openMobileBookingWidget(step) {
      this.$refs.mobileBookingWidget.show(step);
    },
    checkBeforeSubmit() {
      return true
    },
    onBeforeSubmit() {
      const valid = this.$refs.valiObjserver.validate().then(result => {
        if (result === false) {
          setTimeout(() => {
            const errors = JSON.parse(
              JSON.stringify(this.$refs.valiObjserver.errors)
            );

            for (const field of fieldsOrder) {
              if (errors[field] && errors[field].length) {
                this.$refs[field].$el.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center'
                });
                break;
              }
            }
          }, 100); //https://github.com/logaretm/vee-validate/issues/2248
        } else {
          this.onSubmit();
        }
      });
    },
    onSubmit() {
      const canSubmit = this.checkBeforeSubmit();

      if (canSubmit) {
        this.formSubmitted = true;

        const form = document.getElementById('form');
        const thankyou = document.getElementById('thankyou');
        const thankyoumessage = document.getElementById('thankyoumessage');

        const { id, itineraryId } = this.getQueryParams()
        const itinerary_name = get(this.itinerary, 'charter_itinerary.name')
        const supplier_name = get(this.supplier, 'name')
        const image_url = get(this.supplier, 'image')

        const payload = {
          first_name: this.firstName,
          last_name: this.lastName,
          email: this.email,
          phone: this.phone,
          country: this.country,
          nights_num: this.nightsNum,
          check_in: this.checkIn.format('YYYY-MM-DD'),
          guest_note: this.guestNote,
          url: window.location.href,
          session: window.Craft.session,
          supplier_id: id,
          supplier_name,
          itinerary_id: itineraryId,
          itinerary_name,
          image_url
        };

        this.isWorking = true;

        try {
          $.ajax({
            method: 'POST',
            url: window.ZUBLU_VARIABLES.ODYSSEY_API + '/api/v1/okeanos_proxy',
            data: JSON.stringify({
              payload: payload
            }),
            headers: {'Content-Type': 'application/json'}
          });
        }
        catch (error) {}

        $.ajax({
          method: 'POST',
          url: '/',
          data: {
            action: 'zuBlu/public/submitYachtEnquiry',
            [window.Craft.csrfTokenName]: window.Craft.csrfTokenValue,
            formData: payload
          },
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        }).done(() => {
          // sendGAEvent('Submit Enquiry','Contact Us Submit',');

          window.pushDataLayer('enquiry', {
            submit_location: 'main_site',
            submit_type: 'direct_destination',
          });

          thankyoumessage.innerText = `Thank you for your booking enquiry, ${this.firstName}.`;
          form.classList.add('hidden');
          window.scrollTo(0, thankyou);
          thankyou.classList.remove('hidden');
        }).fail(e => {
          window.pushDataLayer('zublu_cta', {
            cta_type: 'enquiry',
            cta_value: 'enquiry_error',
            cta_screen: window.location.pathname
          });

          $.ajax({
            method: 'POST',
            url: window.ZUBLU_VARIABLES.OCTOBOX_API +  '/api/error-notification-enquiry',
            data: JSON.stringify({ enquiry: payload, err_message: e }),
            headers: {
              'Authorization': 'Basic ZGV2ZWxvcG1lbnRAenVibHVkaXZpbmcuY29tOmRldjEyMzQ1',
              'Content-Type': 'application/json'
            }
          })
            .done(() => {
              thankyoumessage.innerText = `Thank you for your booking enquiry, ${this.firstName}.`;
              form.classList.add('hidden');
              window.scrollTo(0, thankyou);
              thankyou.classList.remove('hidden');
            })
            .fail(console.error);
        });
      }
    },
    getSupplierInfo(supplierId, itineraryId) {
      window.fetchSupplierInfo(supplierId)
        .then(({ itineraries = [], supplier }) => {
          this.itinerary = itineraries.find(i => i.id == itineraryId) || {}
          this.supplier = supplier
        })
    },
    getQueryParams() {
      const params = new URLSearchParams(window.location.search)
      return Array.from(params).reduce((params, pair) => {
        const [key, value] = pair
        params[key] = value
        return params
      }, {})
    }
  }
};
</script>

<style lang="scss" scoped>
button {
  .zb-spin {
    display: none;
  }
  &.loading {
    .zb-spin {
      display: inline-block;
    }
  }
}
.font-bry {
  font-family: 'Brygada 1918', serif;
}
</style>
